import { AiChat } from "@/pages/AiChat";
import { AgentMarketplace } from "@/pages/AgentMarketplace";
import { AgentSettings } from "@/pages/AgentSettings";
import { MatchSettingsPage } from "@/pages/MatchSettingsPage";
import { AuthController } from "@/components/login/AuthController";
import { Loading } from "@/components/ui/Loading";
import { useEffect, useRef, useState } from "react";
import { useUserStore } from "@/store/UserStore";
import { authService } from "@/utils/AuthService";
import { login as loginApi } from "@/api/login";
import { getHistoryList } from "@/api/history";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/Sonner";
import ToastEvent from "@/utils/toast-event";


type PageType = 'chat' | 'agents' | 'settings' | 'match-settings';

function isSubcontractorPhoneLoginUrl() {
  const { pathname, search } = window.location;
  if (!pathname.endsWith('/subcontractor')) {
    return null;
  }
  return new URLSearchParams(search).get('phone');
}

function App() {
  const { login, logout, clearAuth } = useUserStore();

  const [currentPage, setCurrentPage] = useState<PageType>('chat');
  const [settingsAgentId, setSettingsAgentId] = useState('');
  const [initialPhone] = useState(() => isSubcontractorPhoneLoginUrl());
  const [isAutoLoggingIn, setIsAutoLoggingIn] = useState(!!initialPhone);
  const autoLoginStarted = useRef(false);

  const clearUrlParams = (...keys: string[]) => {
    const url = new URL(window.location.href);
    keys.forEach((key) => url.searchParams.delete(key));
    window.history.replaceState({}, '', url.toString());
  };

  const navigate = (page: string, agentId?: string) => {
    const normalizedPage = page.startsWith('/') ? page.slice(1) : page as PageType;
    if (normalizedPage === 'chat' || normalizedPage === 'agents' || normalizedPage === 'settings' || normalizedPage === 'match-settings') {
      setCurrentPage(normalizedPage);
      if (agentId) {
        setSettingsAgentId(agentId);
      }
    }
  };

  useEffect(() => {
    if (!initialPhone || autoLoginStarted.current) {
      return;
    }
    autoLoginStarted.current = true;

    const handlePhoneAutoLogin = async () => {
      clearAuth();

      try {
        const response = await loginApi({ username: initialPhone, password: '123456' });
        if (response?.data?.token) {
          await login(response.data.token);
          try {
            const historyResponse = await getHistoryList(1, 15);
            if (historyResponse.data && Array.isArray(historyResponse.data)) {
              window.dispatchEvent(
                new CustomEvent('historyListUpdated', {
                  detail: { historyData: historyResponse.data },
                })
              );
            }
          } catch (historyError) {
            console.error('获取历史记录失败:', historyError);
          }
        } else {
          toast.error('自动登录失败');
        }
      } catch (error) {
        console.error('自动登录失败:', error);
        toast.error('自动登录失败');
      } finally {
        clearUrlParams('phone');
        setIsAutoLoggingIn(false);
      }
    };

    handlePhoneAutoLogin();
  }, [initialPhone, login, clearAuth]);

  useEffect(() => {
    ToastEvent.register({
      error: (msg) => toast.error(msg),
      success: (msg) => toast.success(msg),
      info: (msg) => toast.info(msg),
    });
    authService.triggerLogout = logout;
  }, [logout]);

  return (
    <>
      <div className="relative flex h-screen w-full bg-background text-foreground overflow-hidden">
        {isAutoLoggingIn ? (
          <div className="flex h-full w-full items-center justify-center">
            <Loading size={40} text="登录中..." />
          </div>
        ) : (
          <>
            {currentPage === 'chat' && <AiChat navigate={navigate} />}
            {currentPage === 'agents' && <AgentMarketplace onNavigate={navigate} />}
            {currentPage === 'settings' && <AgentSettings agentId={settingsAgentId} onBack={() => navigate('chat')} />}
            {currentPage === 'match-settings' && <MatchSettingsPage onBack={() => navigate('chat')} />}
          </>
        )}
        <AuthController suppressLoginModal={isAutoLoggingIn} />
        <Toaster position="top-center" />
      </div>
    </>
  );
}

export default App;
